/**
 * CountrySelector Widget
 *
 * Origin/destination country picker for journey configuration.
 * Countries come from `useCountries()` (@tonnie/locale): named and sorted in
 * the reader's language (F34).
 *
 * UX: Each field (origin / destination) is a clickable card that expands
 * an inline searchable dropdown. Selecting a country collapses the dropdown
 * and auto-focuses the next unfilled field.
 */

import { cn, NEXT_DESTINATIONS, OPEN_DESTINATIONS, ROADMAP_DESTINATIONS } from '@tonnie/foundation';
import { useCountryName, useTranslation, useCountries } from '@tonnie/locale';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CountryFlag,
  Input,
  ScrollArea,
} from '@tonnie/ui';
import { ArrowRight, Check, CheckCircle2, ChevronDown, MapPin, Search, X } from '@tonnie/assets';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { WidgetProps } from '@tonnie/copilot';

// ═══════════════════════════════════════════════════════════════════════
// INLINE COUNTRY PICKER (reusable sub-component)
// ═══════════════════════════════════════════════════════════════════════

interface CountryFieldProps {
  label: string;
  value: string | null;
  disabledCode?: string | null;
  /** When set, only these country codes are selectable; the rest are disabled */
  availableCodes?: Set<string>;
  /** Codes launching in R2 — disabled with "Coming Soon" badge */
  comingSoonCodes?: Set<string>;
  /** Codes on the roadmap — disabled with "Soon" badge */
  soonCodes?: Set<string>;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSelect: (code: string) => void;
  variant: 'origin' | 'destination';
}

function CountryField({
  label,
  value,
  disabledCode,
  availableCodes,
  comingSoonCodes,
  soonCodes,
  isOpen,
  onOpen,
  onClose,
  onSelect,
  variant,
}: CountryFieldProps) {
  const { t } = useTranslation();
  // F34 — names in the reader's language, sorted as their language sorts. The
  // flag is rendered from the ISO code by `<CountryFlag>`; `emoji` is not read
  // on the web.
  const { countries, getCountry } = useCountries();
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const country = getCountry(value);

  // Focus the search input when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      // Small delay to let the DOM render the input before focusing
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const list = query
      ? countries.filter(
          (c) => c.name.toLowerCase().includes(query) || c.code.toLowerCase().includes(query)
        )
      : countries;
    if (availableCodes || comingSoonCodes || soonCodes) {
      return [...list].sort((a, b) => {
        const rank = (code: string) => {
          if (availableCodes?.has(code)) return 0;
          if (comingSoonCodes?.has(code)) return 1;
          if (soonCodes?.has(code)) return 2;
          return 3;
        };
        return rank(a.code) - rank(b.code);
      });
    }
    return list;
  }, [search, countries, availableCodes, comingSoonCodes, soonCodes]);

  const handleSelect = useCallback(
    (code: string) => {
      onSelect(code);
      onClose();
    },
    [onSelect, onClose]
  );

  return (
    <div ref={containerRef} className="flex-1 relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => (isOpen ? onClose() : onOpen())}
        className={cn(
          'w-full p-3 rounded-xl border-2 text-left transition-all',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          isOpen
            ? 'border-primary bg-primary/5 shadow-sm'
            : country
              ? 'border-border hover:border-primary/40'
              : 'border-dashed border-muted-foreground/30 hover:border-primary/40'
        )}
      >
        <p className="text-2xs font-medium text-muted-foreground mb-1 uppercase tracking-wider">
          {label}
        </p>
        {country ? (
          <div className="flex items-center gap-2">
            <CountryFlag code={country.code} size="lg" />
            <span className="text-sm font-semibold text-foreground flex-1">{country.name}</span>
            <ChevronDown
              className={cn(
                'w-4 h-4 text-muted-foreground transition-transform',
                isOpen && 'rotate-180'
              )}
            />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
              <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
            </div>
            <span className="text-sm text-muted-foreground flex-1">
              {t('copilot.selectCountry')}
            </span>
            <ChevronDown
              className={cn(
                'w-4 h-4 text-muted-foreground transition-transform',
                isOpen && 'rotate-180'
              )}
            />
          </div>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
          {/* Search */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={inputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('copilot.searchCountry')}
                aria-label={t('form.countrySelect.searchPlaceholder')}
                className="pl-8 pr-8 h-9 text-sm border-0 bg-muted/50 focus-visible:ring-1"
                data-testid="widget-country_selector-search"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={t('form.phoneCountryCode.clearSearch')}
                >
                  <X className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          {/* Country list */}
          <ScrollArea className="max-h-52">
            <div className="p-1">
              {filtered.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  {t('copilot.noCountriesFound')}
                </p>
              ) : (
                filtered.map((c) => {
                  const isSelected = c.code === value;
                  const isSameAsOther = c.code === disabledCode;
                  const isComingSoon = comingSoonCodes?.has(c.code) ?? false;
                  const isSoon = soonCodes?.has(c.code) ?? false;
                  const isUnavailable = availableCodes ? !availableCodes.has(c.code) : false;
                  const isDisabled = isSameAsOther || isUnavailable;
                  return (
                    <button
                      key={c.code}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => handleSelect(c.code)}
                      data-testid={`widget-country_selector-option-${c.code}`}
                      className={cn(
                        'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors text-left',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                        'disabled:cursor-not-allowed disabled:opacity-40',
                        isSelected
                          ? 'bg-primary/10 text-primary-accessible font-medium'
                          : !isDisabled && 'hover:bg-muted/80'
                      )}
                    >
                      <CountryFlag code={c.code} size="md" />
                      <span className="flex-1">{c.name}</span>
                      {isSelected && <Check className="w-4 h-4 text-primary-accessible shrink-0" />}
                      {isComingSoon && (
                        <span className="text-2xs font-medium text-primary-accessible bg-primary/10 px-1.5 py-0.5 rounded">
                          {t('common.comingSoon')}
                        </span>
                      )}
                      {isSoon && !isComingSoon && (
                        <span className="text-2xs font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                          {t('common.soon')}
                        </span>
                      )}
                      {isSameAsOther && !isUnavailable && (
                        <span className="text-2xs text-muted-foreground">
                          {variant === 'origin'
                            ? t('copilot.selectedAsDest')
                            : t('copilot.selectedAsOrigin')}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// MAIN WIDGET
// ═══════════════════════════════════════════════════════════════════════

interface ICountrySelectorConfig {
  /**
   * Which of the widget's OWN translated label pairs to show: the questions
   * ("Where are you from?" — the Journey Builder, the default) or the short
   * field names ("From" / "To" — TelePortal's confirm card, whose description
   * already asks the question). A choice, never copy: guide configs are
   * translated through an allowlist of display keys, so a label passed as
   * text here would reach every citizen in English.
   */
  labels?: 'question' | 'short';
  originCountry?: string;
  destinationCountry?: string;
}

/** Which corridors are open, next, and on the roadmap — `@tonnie/foundation`. */
const AVAILABLE_DESTINATIONS = new Set(OPEN_DESTINATIONS);
const COMING_SOON_DESTINATIONS = new Set(NEXT_DESTINATIONS);
const SOON_DESTINATIONS = new Set(ROADMAP_DESTINATIONS);

/**
 * CountrySelectorWidget - Inline origin/destination country picker.
 *
 * Each field expands an integrated searchable dropdown directly within the card.
 * Selecting a country collapses the dropdown and auto-advances to the next field.
 * Prevents selecting the same country for both origin and destination.
 * R1: Destination restricted to PT only. ES and IT shown as "Coming Soon" (R2).
 * All other countries show "Soon" badge and are disabled.
 */
export function CountrySelectorWidget({
  config,
  onSelect,
}: WidgetProps & { config?: ICountrySelectorConfig; onSelect?: (value: unknown) => void }) {
  const { t } = useTranslation();
  const countryName = useCountryName();

  const initialOrigin = config?.originCountry ?? null;
  const initialDest = config?.destinationCountry ?? null;

  const [origin, setOrigin] = useState<string | null>(initialOrigin);
  const [destination, setDestination] = useState<string | null>(initialDest);
  const [openField, setOpenField] = useState<'origin' | 'destination' | null>(
    initialOrigin ? (initialDest ? null : 'destination') : 'origin'
  );

  const shortLabels = config?.labels === 'short';
  const originLabel = shortLabels ? t('copilot.originShort') : t('copilot.origin');
  const destinationLabel = shortLabels ? t('copilot.destinationShort') : t('copilot.destination');

  const handleOriginSelect = useCallback(
    (code: string) => {
      setOrigin(code);
      // Auto-advance to destination if not filled yet
      if (!destination) {
        setTimeout(() => setOpenField('destination'), 150);
      }
    },
    [destination]
  );

  const handleDestinationSelect = useCallback((code: string) => {
    setDestination(code);
  }, []);

  const handleConfirm = useCallback(() => {
    if (origin && destination) {
      // The names ride along for the guide's own copy ("Route set: Brazil →
      // Portugal"), in the reader's language; the codes are what is stored.
      onSelect?.({
        originCountry: origin,
        destinationCountry: destination,
        originCountryName: countryName(origin),
        destinationCountryName: countryName(destination),
      });
    }
  }, [origin, destination, onSelect, countryName]);

  const canConfirm = origin && destination && origin !== destination;

  return (
    <Card className="w-full border-2 border-border" data-testid="widget-country_selector">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <MapPin className="w-5 h-5 text-primary-accessible" />
          {t('copilot.journeyRoute')}
        </CardTitle>
        <CardDescription>{t('copilot.journeyRouteDesc')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Country fields */}
        <div className="flex items-start gap-3">
          <CountryField
            label={originLabel}
            value={origin}
            disabledCode={destination}
            isOpen={openField === 'origin'}
            onOpen={() => setOpenField('origin')}
            onClose={() => setOpenField(null)}
            onSelect={handleOriginSelect}
            variant="origin"
          />

          <div className="flex items-center justify-center pt-8 shrink-0">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <ArrowRight className="w-4 h-4 text-primary-accessible" />
            </div>
          </div>

          <CountryField
            label={destinationLabel}
            value={destination}
            disabledCode={origin}
            availableCodes={AVAILABLE_DESTINATIONS}
            comingSoonCodes={COMING_SOON_DESTINATIONS}
            soonCodes={SOON_DESTINATIONS}
            isOpen={openField === 'destination'}
            onOpen={() => setOpenField('destination')}
            onClose={() => setOpenField(null)}
            onSelect={handleDestinationSelect}
            variant="destination"
          />
        </div>

        {/* Validation & Confirm */}
        {origin && destination && origin === destination && (
          <p className="text-xs text-destructive-accessible text-center">
            {t('copilot.sameCountryWarning')}
          </p>
        )}

        {canConfirm && (
          <div className="flex justify-end pt-1">
            <Button
              size="sm"
              onClick={handleConfirm}
              className="gap-1.5 animate-in fade-in slide-in-from-bottom-2 duration-200"
              data-testid="widget-country_selector-confirm"
            >
              <CheckCircle2 className="w-4 h-4" />
              {t('common.confirm')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
